<template>
  <div>
    <v-container
      v-if="isLoading"
      class="d-flex justify-center align-center"
      style="height: 100%;"
    >
      <div class="text-center">
        <v-progress-circular
          :size="80"
          indeterminate
          color="#2661FF"
        ></v-progress-circular>
      </div>
    </v-container>
    <v-container v-else>
      <!--------------------------------------------------------->
      <!-- ACTUAL RECURRING ------------------------------------->
      <!--------------------------------------------------------->
      <v-card class="rounded-lg custom_panel pa-2" flat>
        <v-row no-gutters>
          <v-col cols="12" class="d-flex justify-space-around">
            <span class="subtitle-1 text_color">
              {{ $t("subscriptions.edit.actual_recurring") }}
            </span>
            <span class="subtitle-1">
              {{
                subscription["amount"]
                  | valueFormatCurrency(subscription.gateway.currency.symbol)
              }}
            </span>
          </v-col>
        </v-row>
      </v-card>
      <!--------------------------------------------------------->
      <!-- NEW RECURRING ---------------------------------------->
      <!--------------------------------------------------------->
      <v-card class="rounded-lg custom_panel pa-2 my-5" flat>
        <v-card-title class="pa-2">
          <v-row no-gutters>
            <v-col cols="12" class="d-flex justify-center align-center">
              <span class="subtitle-1 text_color">{{
                $t("subscriptions.edit.new_recurring")
              }}</span>
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text>
          <!--------------------------------------------------------->
          <!-- UPDATE AMOUNT ---------------------------------------->
          <!--------------------------------------------------------->
          <v-row no-gutters class="my-4">
            <v-col cols="12" md="6" class="d-flex justify-center align-center">
              <span class="subtitle-1 text_color">
                {{ $t("recurring_debit.details.table.amount") }}
              </span>
            </v-col>
            <v-col cols="12" md="6" class="d-flex justify-center">
              <v-text-field
                v-model="localSubscription.amount"
                v-validate="'required|decimal:2'"
                type="number"
                class="rounded-lg"
                hide-details
                outlined
                dense
                :suffix="localSubscription.gateway.currency.code"
              >
              </v-text-field>
            </v-col>
          </v-row>
          <!--------------------------------------------------------->
          <!-- PAYMENT DATE ---------------------------------------->
          <!--------------------------------------------------------->
          <v-row no-gutters class="my-4">
            <v-col cols="12" md="6" class="d-flex justify-center align-center">
              <span class="subtitle-1 text_color">
                {{ $t("transactions.details.next_donation") }}
              </span>
            </v-col>
            <v-col cols="12" md="6" class="d-flex justify-center">
              <v-text-field
                :value="formatDate(localSubscription.next_payment_date)"
                v-validate="'required|decimal:2'"
                type="date"
                class="rounded-lg"
                append-icon="calendar_month"
                readonly
                hide-details
                outlined
                dense
              >
              </v-text-field>
            </v-col>
          </v-row>
          <!--------------------------------------------------------->
          <!-- BANK ---------------------------------------->
          <!--------------------------------------------------------->
          <v-row no-gutters class="my-4">
            <v-col cols="12" md="6" class="d-flex justify-center align-center">
              <span class="subtitle-1 text_color">
                {{ $t("recurring_debit.details.table.bank") }}
              </span>
            </v-col>
            <v-col cols="12" md="6" class="d-flex justify-center">
              <v-autocomplete
                v-model="bank.bank_id"
                :items="banks"
                item-value="id"
                item-text="attributes.name"
                v-validate="'required'"
                class="rounded-lg"
                hide-details
                outlined
                dense
                :readonly="bank.isNoFunds"
                :data-vv-name="$t('recurring_debit.details.table.bank')"
                :error-messages="
                  errors.collect($t('recurring_debit.details.table.bank'))
                "
              >
              </v-autocomplete>
            </v-col>
          </v-row>
          <!--------------------------------------------------------->
          <!-- ACCOUNT TYPE ---------------------------------------------->
          <!--------------------------------------------------------->
          <v-row no-gutters class="my-4">
            <v-col cols="12" md="6" class="d-flex justify-center align-center">
              <span class="subtitle-1 text_color">
                {{ $t("transactions.details.paymentMethod") }}
              </span>
            </v-col>
            <v-col cols="12" md="6" class="d-flex justify-center">
              <v-autocomplete
                v-model="bank.account_type_id"
                :items="accountTypes"
                item-value="id"
                item-text="attributes.name"
                v-validate="'required'"
                class="rounded-lg"
                hide-details
                outlined
                dense
                :readonly="bank.isNoFunds"
                :data-vv-name="$t('transactions.details.paymentMethod')"
                :error-messages="
                  errors.collect($t('transactions.details.paymentMethod'))
                "
              >
              </v-autocomplete>
            </v-col>
          </v-row>
          <!--------------------------------------------------------->
          <!-- PAYER ACCOUNT NUMBER ---------------------------------------------->
          <!--------------------------------------------------------->
          <v-row no-gutters class="my-4">
            <v-col cols="12" md="6" class="d-flex justify-center align-center">
              <span class="subtitle-1 text_color">
                {{ $t("recurring_debit.details.table.account_number") }}
              </span>
            </v-col>
            <v-col cols="12" md="6" class="d-flex justify-center">
              <v-text-field
                v-model="bank.payer_account_number"
                v-validate="'required'"
                class="rounded-lg"
                hide-details
                outlined
                dense
                :readonly="bank.isNoFunds"
                :data-vv-name="$t('transactions.details.paymentMethod')"
                :error-messages="
                  errors.collect($t('transactions.details.paymentMethod'))
                "
              >
              </v-text-field>
            </v-col>
          </v-row>
          <!--------------------------------------------------------->
          <!-- PAYER ID TYPE ---------------------------------------------->
          <!--------------------------------------------------------->
          <v-row no-gutters class="my-4">
            <v-col cols="12" md="6" class="d-flex justify-center align-center">
              <span class="subtitle-1 text_color">
                {{ $t("people.document_type") }}
              </span>
            </v-col>
            <v-col cols="12" md="6" class="d-flex justify-center">
              <v-autocomplete
                v-model="bank.payer_id_type_id"
                :items="payerTypes"
                item-value="id"
                item-text="attributes.name"
                v-validate="'required'"
                class="rounded-lg"
                hide-details
                outlined
                dense
                :readonly="bank.isNoFunds"
                :data-vv-name="$t('people.document_type')"
                :error-messages="errors.collect($t('people.document_type'))"
              >
              </v-autocomplete>
            </v-col>
          </v-row>
          <!--------------------------------------------------------->
          <!-- PAYER ID ---------------------------------------------->
          <!--------------------------------------------------------->
          <v-row no-gutters class="my-4">
            <v-col cols="12" md="6" class="d-flex justify-center align-center">
              <span class="subtitle-1 text_color">
                {{ $t("people.document") }}
              </span>
            </v-col>
            <v-col cols="12" md="6" class="d-flex justify-center">
              <v-text-field
                v-model="bank.identification"
                v-validate="'required'"
                class="rounded-lg"
                hide-details
                outlined
                dense
                :readonly="bank.isNoFunds"
                :data-vv-name="$t('people.document')"
                :error-messages="errors.collect($t('people.document'))"
              >
              </v-text-field>
            </v-col>
          </v-row>
          <!--------------------------------------------------------->
          <!-- PAYMENT METHOD ---------------------------------------------->
          <!--------------------------------------------------------->
          <v-row no-gutters class="my-4">
            <v-col cols="12" md="6" class="d-flex justify-center align-center">
              <span class="subtitle-1 text_color">
                {{ $t("transactions.details.paymentMethod") }}
              </span>
            </v-col>
            <v-col cols="12" md="6" class="d-flex justify-center">
              <v-icon color="#344054">
                {{ getSubscriptionPaymentMethod(subscription).icon }}
              </v-icon>
              <span class="text_color body-2 mt-1 mx-1">
                {{ getSubscriptionPaymentMethod(subscription).text }}
              </span>
            </v-col>
          </v-row>
          <!--------------------------------------------------------->
          <!-- GATEWAY ---------------------------------------------->
          <!--------------------------------------------------------->
          <v-row no-gutters class="my-4">
            <v-col cols="12" md="6" class="d-flex justify-center align-center">
              <span class="subtitle-1 text_color">
                {{ $t("transactions.details.gateway") }}
              </span>
            </v-col>
            <v-col cols="12" md="6" class="d-flex justify-center align-center">
              <v-card
                class="rounded-lg d-flex justify-center align-center py-2"
                style="border: 1.5px solid #9d9e9e; width: 100%;"
                flat
              >
                <span class="subtitle-1 text_color">
                  {{ localSubscription.gateway.gateway.name }}
                </span>
                <v-img
                  :src="getGatewayImage()"
                  alt="Gateway image"
                  class="ml-2"
                  max-height="70"
                  max-width="80"
                />
              </v-card>
            </v-col>
          </v-row>
          <!--------------------------------------------------------->
          <!-- INFO ------------------------------------------------->
          <!--------------------------------------------------------->
          <v-row no-gutters class="mt-8">
            <v-col cols="12" class="d-flex justify-center align-center">
              <span class="body-2 text_color text-center">
                {{ $t("subscriptions.edit.info") }}
              </span>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-row no-gutters>
            <v-col cols="12" class="d-flex justify-end align-center">
              <v-btn
                class="rounded-lg"
                color="#2661FF"
                @click="save"
                :loading="isSubmitLoading"
                :disabled="!canUpdateSubscription"
              >
                <span style="color: #FFF;">
                  {{ $t("subscriptions.edit.update") }}
                </span>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import GatewaysMixin from "@/mixins/gateways";

export default {
  name: "EditRecurrentPaymentsSubscription",
  mixins: [GatewaysMixin],
  data() {
    return {
      isLoading: false,
      isSubmitLoading: false,
      localSubscription: {},
      bank: {
        payer_name: null,
        payer_account_number: null,
        identification: null,
        bank_id: null,
        account_type_id: null,
        payer_id_type_id: null,
        isNoFunds: false
      },
      banks: [],
      accountTypes: [],
      payerTypes: []
    };
  },
  async created() {
    // Copy the subscription state
    this.localSubscription = {
      ...this.subscription
    };

    await this.inicialice();
  },
  computed: {
    ...mapState({
      subscription: ({ transactions: { transaction } }) => transaction
    }),
    canUpdateSubscription() {
      const { gateway } = this.subscription.gateway;

      return gateway.info.includes("EDIT_SUBSCRIPTION");
    }
  },
  methods: {
    ...mapActions({
      updateSubscription: "transactions/updateSubscription"
    }),
    async inicialice() {
      const { localSubscription } = this;
      const { gatawey_json_response, metadata } = localSubscription;
      const { attributes, relationships } = gatawey_json_response;

      if (attributes) {
        this.getSubscriptionAttributes(attributes);
      }
      if (relationships) {
        this.getSubscriptionRelationships(relationships);
      }
      if (metadata) {
        this.getSubscriptionMetadata(metadata);
      }

      this.isLoading = true;

      await this.getBanks();
      await this.getAccountTypes();
      await this.getPayerTypes();

      this.isLoading = false;
    },
    getSubscriptionAttributes(attributes) {
      const { payer_account_number, payer_identification } = attributes;

      this.bank.payer_account_number = payer_account_number;
      this.bank.identification = payer_identification;
    },
    getSubscriptionRelationships(relationships) {
      const { bank, account_type, payer_id_type } = relationships;

      if (bank) {
        const { data } = bank;
        if (data && data.id) {
          this.bank.bank_id = data.id;
        }
      }
      if (account_type) {
        const { data } = account_type;
        if (data && data.id) {
          this.bank.account_type_id = data.id;
        }
      }
      if (payer_id_type) {
        const { data } = payer_id_type;
        if (data && data.id) {
          this.bank.payer_id_type_id = data.id;
        }
      }
    },
    getSubscriptionMetadata(metadata) {
      const { reason } = metadata;

      if (reason && reason == "not_funds") {
        this.isNoFunds = true;
      }
    },
    async getBanks() {
      const { organization_id } = this.subscription;
      try {
        const banks = (await axios.get(`o/${organization_id}/achgateway/banks`))
          .data;
        this.banks = banks;
      } catch (error) {
        console.log(error);
      }
    },
    async getAccountTypes() {
      const { organization_id } = this.subscription;
      try {
        const accountTypes = (
          await axios.get(`o/${organization_id}/achgateway/accounts`)
        ).data;
        this.accountTypes = accountTypes;
      } catch (error) {
        console.log(error);
      }
    },
    async getPayerTypes() {
      const { organization_id } = this.subscription;
      try {
        const payerTypes = (
          await axios.get(`o/${organization_id}/achgateway/payeridtypes`)
        ).data;
        this.payerTypes = payerTypes;
      } catch (error) {
        console.log(error);
      }
    },
    getGatewayImage() {
      const { gateway } = this.subscription.gateway;
      return this.getGatewayImageByName(gateway.name);
    },
    getSubscriptionPaymentMethod(subscription) {
      const CARD = 1;

      switch (subscription.payment_method) {
        case CARD:
          return {
            text: this.$t("transactions.details.creditCard"),
            icon: "credit_card"
          };
        default:
          return {
            text: this.$t("transactions.details.bank_account"),
            icon: "account_balance"
          };
      }
    },
    formatDate(date) {
      return this.$moment(date).format("YYYY-MM-DD");
    },
    async save() {
      const {
        gateway,
        transaction_gateway_code,
        gateway_currency_id,
        organization_id
      } = this.localSubscription;

      const IDENTIFICATION_TYPES = {
        NATIONAL_IDENTIFICATION_DOCUMENT_FOREIGN: 5,
        NATIONAL_IDENTIFICATION_DOCUMENT: 4,
        NIT: 6
      };

      const {
        payer_account_number,
        bank_id,
        account_type_id,
        payer_id_type_id,
        identification: payer_identification
      } = this.bank;

      try {
        this.isSubmitLoading = true;

        const valid = await this.$validator.validateAll();

        if (!valid) throw new Error("Invalid fields");

        let identification_type =
          IDENTIFICATION_TYPES.NATIONAL_IDENTIFICATION_DOCUMENT_FOREIGN;
        if (this.payer_id_type_id === "1") {
          identification_type =
            IDENTIFICATION_TYPES.NATIONAL_IDENTIFICATION_DOCUMENT;
        } else if (this.payer_id_type_id === "2") {
          identification_type = IDENTIFICATION_TYPES.NIT;
        }

        const person_object = {
          identification: payer_identification,
          identification_type
        };
        const payment_object = {
          payer_account_number,
          bank_id,
          account_type_id,
          payer_id_type_id,
          payer_identification
        };

        await this.updateSubscription({
          ...this.localSubscription,
          id: this.localSubscription.id,
          gateway_id: gateway.gateway.id,
          subscription_id: transaction_gateway_code,
          gateway_currency_id,
          organization_id,
          payment_object,
          person_object
        });
      } catch (error) {
        this.$noty.error(
          `${this.$t("subscriptions.edit.error")} ${error.message}`
        );
      } finally {
        this.isSubmitLoading = false;
      }
    }
  }
};
</script>

<style>
.text_color {
  color: #344054 !important;
}

.custom_panel {
  border: 1px solid #d0d3db !important;
}
</style>
